/**
 * Modal Component
 *
 * Overlay dialog for quick actions.
 * Supports closing via backdrop click or escape key.
 */

import { ReactNode, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './Modal.css';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'full';
  preventClose?: boolean; // Prevent closing (during critical operations)
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  preventClose = false,
}: ModalProps) {
  // Handle escape key (respect preventClose flag)
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !preventClose) {
        onClose();
      }
    },
    [onClose, preventClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Respect preventClose flag (don't close on backdrop click during critical operations)
    if (e.target === e.currentTarget && !preventClose) {
      onClose();
    }
  };

  // Using inline styles - CSS classes weren't being applied correctly
  const backdropStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    animation: 'fadeIn 0.2s ease',
  };

  // Size-based max-width
  const sizeWidths = {
    sm: '360px',
    md: '480px',
    lg: '600px',
    full: '90vw',
  };

  const modalStyle: React.CSSProperties = {
    background: '#0f0f14',
    borderRadius: '12px',
    border: '1px solid #252530',
    padding: 0,
    minWidth: '320px',
    maxWidth: sizeWidths[size],
    width: '95%',
    maxHeight: size === 'lg' || size === 'full' ? '90vh' : '85vh',
    overflowY: 'auto',
    color: '#e8e8f0',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
    position: 'relative',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    borderBottom: '1px solid #252530',
    background: '#141419',
    borderRadius: '12px 12px 0 0',
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: '#8888a0',
    fontSize: '1.5rem',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '6px',
    lineHeight: 1,
  };

  // Floating close button for modals without title
  const floatingCloseStyle: React.CSSProperties = {
    position: 'absolute',
    top: '12px',
    right: '12px',
    width: '36px',
    height: '36px',
    background: 'rgba(20, 20, 25, 0.9)',
    border: '1px solid #252530',
    borderRadius: '50%',
    color: '#8888a0',
    fontSize: '1.25rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    transition: 'all 0.15s ease',
  };

  return createPortal(
    <div style={backdropStyle} onClick={handleBackdropClick}>
      <div style={modalStyle} role="dialog" aria-modal="true">
        {title ? (
          <div style={headerStyle}>
            <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600 }}>{title}</h2>
            <button
              onClick={onClose}
              style={closeButtonStyle}
              aria-label="Close modal"
            >
              ×
            </button>
          </div>
        ) : (
          <button
            onClick={onClose}
            style={floatingCloseStyle}
            aria-label="Close modal"
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#252530';
              e.currentTarget.style.color = '#e8e8f0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(20, 20, 25, 0.9)';
              e.currentTarget.style.color = '#8888a0';
            }}
          >
            ×
          </button>
        )}
        <div style={{ padding: '16px' }}>{children}</div>
      </div>
    </div>,
    document.body
  );
}
